import gql from "graphql-tag";
import * as React from "react";
import { Mutation, Query } from "react-apollo";

export const productReviewsQuery = gql`
  query ProductReviews($productId: ID!) {
    productReviewSummary(productId: $productId) {
      averageRating
      reviewCount
    }
    productReviews(productId: $productId, first: 20) {
      id
      rating
      title
      body
      authorLabel
      verifiedPurchase
      createdAt
    }
    productReviewEligibility(productId: $productId) {
      canReview
      reason
    }
    myProductReview(productId: $productId) {
      id
      rating
      title
      body
      status
      rejectionReason
      updatedAt
    }
  }
`;

export const productReviewSubmitMutation = gql`
  mutation ProductReviewSubmit(
    $productId: ID!
    $rating: Int!
    $title: String
    $body: String
  ) {
    productReviewSubmit(
      productId: $productId
      rating: $rating
      title: $title
      body: $body
    ) {
      review {
        id
        status
      }
    }
  }
`;

interface Review {
  id: string;
  rating: number;
  title: string;
  body: string;
  authorLabel: string;
  verifiedPurchase: boolean;
  createdAt: string;
}

interface MyReview {
  id: string;
  rating: number;
  title: string;
  body: string;
  status: string;
  rejectionReason: string;
  updatedAt: string;
}

interface ReviewsData {
  productReviewSummary: {
    averageRating: number | null;
    reviewCount: number;
  } | null;
  productReviews: Review[];
  productReviewEligibility: { canReview: boolean; reason: string } | null;
  myProductReview: MyReview | null;
}

export const reviewStars = (rating: number) =>
  "★★★★★".slice(0, rating) + "☆☆☆☆☆".slice(0, 5 - rating);

const ReviewForm: React.FC<{
  productId: string;
  myReview: MyReview | null;
  onSaved: () => Promise<any>;
}> = ({ productId, myReview, onSaved }) => {
  const [rating, setRating] = React.useState(myReview ? myReview.rating : 5);
  const [title, setTitle] = React.useState(myReview ? myReview.title : "");
  const [body, setBody] = React.useState(myReview ? myReview.body : "");
  const [feedback, setFeedback] = React.useState("");

  React.useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setTitle(myReview.title);
      setBody(myReview.body);
    }
  }, [myReview && myReview.id, myReview && myReview.updatedAt]);

  return (
    <Mutation mutation={productReviewSubmitMutation}>
      {(submit, { loading }) => (
        <form
          className="product-reviews__form"
          onSubmit={async event => {
            event.preventDefault();
            setFeedback("");
            try {
              const result = await submit({
                variables: { productId, rating, title, body },
              });
              await onSaved();
              const status =
                result &&
                result.data &&
                result.data.productReviewSubmit &&
                result.data.productReviewSubmit.review.status;
              setFeedback(
                status === "approved"
                  ? "Your verified rating is now visible."
                  : "Thank you. Your written review is awaiting staff approval."
              );
            } catch (error) {
              setFeedback(
                error.message || "Review could not be saved. Try again."
              );
            }
          }}
        >
          <h3>{myReview ? "Update your review" : "Rate this product"}</h3>
          <label htmlFor="product-review-rating">Your rating</label>
          <select
            id="product-review-rating"
            value={rating}
            onChange={event => setRating(Number(event.target.value))}
          >
            {[5, 4, 3, 2, 1].map(value => (
              <option key={value} value={value}>
                {value} {value === 1 ? "star" : "stars"}
              </option>
            ))}
          </select>
          <label htmlFor="product-review-title">Title (optional)</label>
          <input
            id="product-review-title"
            maxLength={120}
            value={title}
            onChange={event => setTitle(event.target.value)}
          />
          <label htmlFor="product-review-body">
            Your experience (optional)
          </label>
          <textarea
            id="product-review-body"
            maxLength={5000}
            rows={4}
            value={body}
            onChange={event => setBody(event.target.value)}
          />
          <p>
            Written reviews are checked for content-policy issues before they
            appear. Positive and negative product experiences are equally
            welcome. Editing a published review sends new text back for
            approval.
          </p>
          <button disabled={loading} type="submit">
            {loading ? "Saving…" : "Submit verified review"}
          </button>
          {feedback && <p aria-live="polite">{feedback}</p>}
        </form>
      )}
    </Mutation>
  );
};

const ProductReviews: React.FC<{ productId: string }> = ({ productId }) => (
  <section className="product-reviews" aria-labelledby="product-reviews-title">
    <h2 id="product-reviews-title">Customer ratings &amp; reviews</h2>
    <Query<ReviewsData, { productId: string }>
      query={productReviewsQuery}
      variables={{ productId }}
      fetchPolicy="network-only"
    >
      {({ data, error, loading, refetch }) => {
        if (loading && !data) {
          return <p>Loading customer reviews…</p>;
        }
        if (error || !data) {
          return <p>Reviews are temporarily unavailable.</p>;
        }
        const summary = data.productReviewSummary;
        const reviews = data.productReviews || [];
        const eligibility = data.productReviewEligibility;
        const mine = data.myProductReview;
        return (
          <>
            {summary &&
            summary.reviewCount > 0 &&
            summary.averageRating !== null ? (
              <p className="product-reviews__summary">
                <span
                  aria-label={`${summary.averageRating.toFixed(
                    1
                  )} out of 5 stars`}
                >
                  {reviewStars(Math.round(summary.averageRating))}
                </span>{" "}
                {summary.averageRating.toFixed(1)} / 5 from{" "}
                {summary.reviewCount} verified{" "}
                {summary.reviewCount === 1 ? "customer" : "customers"}
              </p>
            ) : (
              <p>No customer ratings yet.</p>
            )}
            {reviews.map(review => (
              <article className="product-reviews__item" key={review.id}>
                <p aria-label={`${review.rating} out of 5 stars`}>
                  {reviewStars(review.rating)}
                </p>
                {review.title && <h3>{review.title}</h3>}
                {review.body && <p>{review.body}</p>}
                <small>
                  {review.authorLabel} ·{" "}
                  {new Date(review.createdAt).toLocaleDateString()}
                </small>
              </article>
            ))}
            {mine && mine.status === "pending" && (
              <p aria-live="polite">
                Your written review is awaiting approval.
              </p>
            )}
            {mine && mine.status === "rejected" && (
              <p aria-live="polite">
                Your review needs a content-policy edit before it can appear.
              </p>
            )}
            {eligibility && eligibility.canReview ? (
              <ReviewForm
                productId={productId}
                myReview={mine}
                onSaved={() => refetch()}
              />
            ) : (
              <p>
                Only signed-in customers with a fulfilled purchase can rate this
                product. {eligibility && eligibility.reason}
              </p>
            )}
          </>
        );
      }}
    </Query>
  </section>
);

export default ProductReviews;
